import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt, faWallet, faGamepad, faChevronDown, faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { GiBull } from 'react-icons/gi';
import {
  getBalance,
  getGames,
  getGameBalances,
  getTransactions,
  swapToGameBalance,
  withdrawFromGameBalance,
} from '../../services/swapFundsService';
import styles from './SwapFunds.module.css';

const TRANSACTIONS_PER_PAGE = 10;

// Mapea tipos a variantes de color
const TYPE_TO_VARIANT = {
  deposit: 'deposit',
  withdraw: 'withdraw',
};

const SwapFunds = () => {
  const { t } = useTranslation();

  // data
  const [globalBalance, setGlobalBalance] = useState(0);
  const [games, setGames] = useState([]);
  const [gameBalances, setGameBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // ui / form
  const [selectedGame, setSelectedGame] = useState('');
  const [amount, setAmount] = useState('');
  const [action, setAction] = useState('deposit');
  const [isBalancesOpen, setIsBalancesOpen] = useState(false);
  const [isTransactionsOpen, setIsTransactionsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // success | danger | warning

  const mountedRef = useRef(true);

  // MENEL formatter (con icono y variante)
  const formatMENEL = useCallback((val, variant = 'neutral') => {
    const n = Number(val || 0);
    return (
      <span className={`${styles.menelWrap} ${styles[`menel--${variant}`]}`}>
        <span className={styles.menelValue}>{n.toFixed(2)}</span>
        <GiBull className={styles.menelIcon} aria-hidden />
        <span className={styles.menelUnit}>MENEL</span>
      </span>
    );
  }, []);

  // fetch initial
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        setLoading(true);
        const [balanceResponse, gamesResponse, gbResponse, txResponse] = await Promise.all([
          getBalance(), getGames(), getGameBalances(), getTransactions()
        ]);
        if (!mountedRef.current) return;
        setGlobalBalance(balanceResponse?.availableBalance || 0);
        setGames(gamesResponse || []);
        setGameBalances(gbResponse || []);
        setTransactions(txResponse || []);
      } catch {
        if (!mountedRef.current) return;
        setMessage(t('swapFunds.errors.fetchError'));
        setMessageType('danger');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();
    return () => { mountedRef.current = false; };
  }, [t]);

  // derived
  const selectedGameBalance = useMemo(() => {
    if (!selectedGame) return null;
    const gb = gameBalances.find(g => +g.game_id === +selectedGame);
    return gb ? Number(gb.balance) : 0;
  }, [selectedGame, gameBalances]);

  const totalGameBalance = useMemo(
    () => gameBalances.reduce((acc, gb) => acc + Number(gb.balance || 0), 0),
    [gameBalances]
  );

  const overallTotal = useMemo(() => globalBalance + totalGameBalance, [globalBalance, totalGameBalance]);

  const shortenTxid = useCallback((txid) => {
    if (!txid) return '';
    return txid.length <= 16 ? txid : `${txid.slice(0, 8)}…${txid.slice(-8)}`;
  }, []);

  const refreshData = useCallback(async () => {
    try {
      const [b, gbs, txs] = await Promise.all([getBalance(), getGameBalances(), getTransactions()]);
      if (!mountedRef.current) return;
      setGlobalBalance(b?.availableBalance || 0);
      setGameBalances(gbs || []);
      setTransactions(txs || []);
    } catch {}
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;

    setMessage('');
    const parsedAmount = Number(amount);
    if (!selectedGame || isNaN(parsedAmount) || parsedAmount <= 0) {
      setMessage(t('swapFunds.errors.selectGameAmount'));
      setMessageType('warning');
      return;
    }
    if (action === 'deposit' && parsedAmount > globalBalance) {
      setMessage(t('swapFunds.errors.insufficientGlobal'));
      setMessageType('danger');
      return;
    }
    if (action === 'withdraw' && (selectedGameBalance === null || parsedAmount > selectedGameBalance)) {
      setMessage(t('swapFunds.errors.insufficientGame'));
      setMessageType('danger');
      return;
    }

    try {
      setBusy(true);
      const payload = { game_id: selectedGame, amount: parsedAmount };
      const res = action === 'deposit'
        ? await swapToGameBalance(payload)
        : await withdrawFromGameBalance(payload);

      setMessage(res?.message || t('swapFunds.success'));
      setMessageType('success');
      setAmount('');
      setSelectedGame('');
      await refreshData();
    } catch (error) {
      setMessage(error?.response?.data?.error || t('swapFunds.errors.processError'));
      setMessageType('danger');
    } finally {
      setBusy(false);
    }
  };

  // CSV helpers
  const csvEscape = (val) => {
    const s = String(val ?? '');
    const needsQuotes = /[",\n;]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

  const buildCSV = useCallback((rows) => {
    // columnas: Fecha ISO, Tipo, Juego/Txid, Importe (MENEL), Balance después (MENEL)
    const headers = ['date', 'type', 'game_or_txid', 'amount_menel', 'balance_after_menel'];
    const lines = [headers.join(',')];

    rows.forEach((tx) => {
      const isHash = /^[a-f0-9]{32,64}$/i.test(tx.txid || '');
      const gameOrTxid = isHash ? tx.txid : (tx.txid || tx.game_name || '');
      const dateIso = tx.transaction_date ? new Date(tx.transaction_date).toISOString() : '';
      const amountStr = `${Number(tx.amount || 0).toFixed(2)} MENEL`;
      const afterStr = `${Number(tx.balance_after || 0).toFixed(2)} MENEL`;
      const row = [
        csvEscape(dateIso),
        csvEscape(tx.transaction_type),
        csvEscape(gameOrTxid),
        csvEscape(amountStr),
        csvEscape(afterStr),
      ].join(',');
      lines.push(row);
    });

    return lines.join('\n');
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!transactions?.length) return;
    const csv = buildCSV(transactions);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    a.href = url;
    a.download = `menel-transactions-${yyyy}-${mm}-${dd}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [transactions, buildCSV]);

  // pagination
  const totalPages = Math.max(1, Math.ceil(transactions.length / TRANSACTIONS_PER_PAGE));
  const pageSafe = Math.min(Math.max(1, currentPage), totalPages);
  const pageTx = useMemo(() => {
    const start = (pageSafe - 1) * TRANSACTIONS_PER_PAGE;
    return transactions.slice(start, start + TRANSACTIONS_PER_PAGE);
  }, [transactions, pageSafe]);

  if (loading) {
    return (
      <div className={styles.bg}>
      <div className={styles.container}>
        <div className={styles.titleSkeleton} />
        <div className={styles.summarySkeleton} />
        <div className={styles.cardSkeleton} />
        </div>
         </div>
    );
  }

  return (
    <div className={styles.bg}>
    <div className={styles.container}>
      <h2 className={styles.title}>
        <FontAwesomeIcon icon={faExchangeAlt} /> {t('swapFunds.title')}
      </h2>

      <section className={styles.summary}>
        <div className={styles.summaryItem}>
          <p className={styles.label}><FontAwesomeIcon icon={faWallet} /> {t('swapFunds.freeBalance')}</p>
          <p className={styles.value}>{formatMENEL(globalBalance, 'neutral')}</p>
        </div>
        <div className={styles.summaryItem}>
          <p className={styles.label}><FontAwesomeIcon icon={faGamepad} /> {t('swapFunds.balanceInGames')}</p>
          <p className={styles.value}>{formatMENEL(totalGameBalance, 'neutral')}</p>
        </div>
        <div className={`${styles.summaryItem} ${styles.total}`}>
          <p className={styles.label}>{t('swapFunds.totalBalance')}</p>
          <p className={styles.value}>{formatMENEL(overallTotal, 'neutral')}</p>
        </div>
      </section>

      <section className={styles.card}>
        <form onSubmit={onSubmit} className={styles.form} noValidate>
          <div className={styles.formRow}>
            <label htmlFor="actionSelect" className={styles.label}>{t('swapFunds.actionLabel')}</label>
            <select
              id="actionSelect"
              className={styles.select}
              value={action}
              onChange={(e) => setAction(e.target.value)}
              disabled={busy}
            >
              <option value="deposit">{t('swapFunds.deposit')}</option>
              <option value="withdraw">{t('swapFunds.withdraw')}</option>
            </select>
          </div>

          <div className={styles.formRow}>
            <label htmlFor="gameSelect" className={styles.label}>
              <FontAwesomeIcon icon={faGamepad} /> {t('swapFunds.selectGame')}
            </label>
            <select
              id="gameSelect"
              className={styles.select}
              value={selectedGame}
              onChange={(e) => setSelectedGame(e.target.value)}
              required
              disabled={busy}
            >
              <option value="">{t('swapFunds.selectGamePlaceholder')}</option>
              {games.map((g) => (
                <option key={g.id} value={g.id}>{g.game_name}</option>
              ))}
            </select>

            {selectedGame && selectedGameBalance !== null && (
              <div className={styles.selectedGameBalance}>
                {t('swapFunds.currentGameBalance')}: <strong>{formatMENEL(selectedGameBalance, 'neutral')}</strong>
              </div>
            )}
          </div>

          <div className={styles.formRow}>
            <label htmlFor="amount" className={styles.label}>{t('swapFunds.amountLabel')}</label>
            <div className={styles.amountRow}>
              <input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                className={styles.input}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                disabled={busy}
                placeholder="0.00"
              />
              <div className={`${styles.inlineUnit} ${styles[`menel--${action}`]}`}>
                <GiBull className={styles.menelIcon} aria-hidden />
                <span>MENEL</span>
              </div>
            </div>
            <div className={styles.quickRow}>
              {[1, 5, 10, 25, 50, 100].map(v => (
                <button
                  type="button"
                  key={v}
                  className={styles.quickBtn}
                  onClick={() => setAmount(String((Number(amount) || 0) + v))}
                  disabled={busy}
                >
                  +{v}
                </button>
              ))}
              <button
                type="button"
                className={styles.quickBtn}
                onClick={() => {
                  if (action === 'deposit') setAmount(String(globalBalance.toFixed(2)));
                  else if (selectedGameBalance != null) setAmount(String(selectedGameBalance.toFixed(2)));
                }}
                disabled={busy || (action === 'withdraw' && selectedGameBalance == null)}
                title="Max"
              >
                Max
              </button>
            </div>
          </div>

          <button type="submit" className={styles.cta} disabled={busy}>
            {busy ? t('common.loading') : action === 'deposit'
              ? t('swapFunds.depositButton')
              : t('swapFunds.withdrawButton')}
          </button>

          {message && (
            <div className={`${styles.message} ${styles[messageType]}`} role="status" aria-live="polite">
              {message}
            </div>
          )}
        </form>
      </section>

      {/* Game balances */}
      <section className={styles.block}>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setIsBalancesOpen(v => !v)}
          aria-expanded={isBalancesOpen}
        >
          <h3 className={styles.blockTitle}>{t('swapFunds.yourGameBalances')}</h3>
          <FontAwesomeIcon icon={isBalancesOpen ? faChevronUp : faChevronDown} />
        </button>

        {isBalancesOpen && (
          <div className={styles.tableWrap}>
            {gameBalances.length === 0 ? (
              <p className={styles.empty}>{t('swapFunds.noGameBalances')}</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('swapFunds.game')}</th>
                    <th>{t('swapFunds.balance')}</th>
                    <th>{t('swapFunds.lastUpdated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {gameBalances.map((gb) => (
                    <tr key={gb.id}>
                      <td>{gb.game_name}</td>
                      <td>{formatMENEL(gb.balance, 'neutral')}</td>
                      <td>{new Date(gb.updated_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {/* Transactions */}
      <section className={styles.block}>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setIsTransactionsOpen(v => !v)}
          aria-expanded={isTransactionsOpen}
        >
          <h3 className={styles.blockTitle}>{t('swapFunds.yourTransactions')}</h3>
          <FontAwesomeIcon icon={isTransactionsOpen ? faChevronUp : faChevronDown} />
        </button>

        {isTransactionsOpen && (
          <>
            <div className={styles.exportBar}>
              <button
                type="button"
                className={styles.exportBtn}
                onClick={handleExportCSV}
                disabled={!transactions.length}
                title="Exportar CSV"
              >
                CSV
              </button>
            </div>

            <div className={styles.tableWrap}>
              {transactions.length === 0 ? (
                <p className={styles.empty}>{t('swapFunds.noTransactions')}</p>
              ) : (
                <>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('swapFunds.date')}</th>
                        <th>{t('swapFunds.type')}</th>
                        <th>{t('swapFunds.gameOrTxid')}</th>
                        <th>{t('swapFunds.amount')}</th>
                        <th>{t('swapFunds.balanceAfter')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageTx.map((tx) => {
                        const isHash = /^[a-f0-9]{32,64}$/i.test(tx.txid || '');
                        const variant = TYPE_TO_VARIANT[tx.transaction_type] || 'neutral';
                        return (
                          <tr key={tx.id}>
                            <td>{new Date(tx.transaction_date).toLocaleString()}</td>
                            <td>{tx.transaction_type}</td>
                            <td>
                              {isHash ? (
                                <a
                                  className={styles.link}
                                  href={`https://explorer.menelik.cc/txid/${tx.txid}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {shortenTxid(tx.txid)}
                                </a>
                              ) : (tx.txid || '—')}
                            </td>
                            <td className={styles[`amount--${variant}`]}>{formatMENEL(tx.amount, variant)}</td>
                            <td className={styles[`amount--${variant}`]}>{formatMENEL(tx.balance_after, variant)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className={styles.pagination}>
                    <button
                      type="button"
                      className={styles.pageBtn}
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={pageSafe === 1}
                    >
                      Prev
                    </button>
                    <span className={styles.pageInfo}>
                      Page {pageSafe} / {totalPages}
                    </span>
                    <button
                      type="button"
                      className={styles.pageBtn}
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      disabled={pageSafe === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </section>
    </div>
    </div>
  );
};

export default SwapFunds;
